import { useState, useEffect } from "react";
import { Camera, Image as ImageIcon, Plus, Trash2, Lock, ChevronDown, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useProfile } from "../context/ProfileContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

function nowLocalISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

// Parse numbers accepting both comma and dot decimals.
function parseNum(v) {
  if (v === "" || v == null) return null;
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isNaN(n) ? null : n;
}

const MODES = [
  { id: "photo", key: "meal_mode_photo" },
  { id: "per_100g", key: "meal_mode_100" },
  { id: "direct", key: "meal_mode_direct" },
];

const EXTRA_DEFS = [
  ["energy_kcal", "Energia", "kcal"],
  ["protein_g", "Proteínas", "g"],
  ["fat_g", "Gordura total", "g"],
  ["saturated_fat_g", "Gordura saturada", "g"],
  ["sugars_g", "Açúcares", "g"],
  ["fiber_g", "Fibra", "g"],
  ["salt_g", "Sal", "g"],
];

function emptyItem(mode) {
  return { name: "", mode, carbs_per_100g: "", grams: "", direct_carbs: "", photo_carbs: null };
}

export default function MealForm({ onSaved, record }) {
  const { t } = useI18n();
  const { activeProfileId } = useProfile();
  const [mode, setMode] = useState("per_100g");
  const [photoMode, setPhotoMode] = useState("single");
  const [items, setItems] = useState([emptyItem("per_100g")]);
  const [dt, setDt] = useState(nowLocalISO());
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState(null);
  const [showExtras, setShowExtras] = useState(false);
  const [demoResult, setDemoResult] = useState(false);
  const [aiResult, setAiResult] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [extras, setExtras] = useState(null);
  const [extrasSource, setExtrasSource] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/ai/status").then(({ data }) => setAiEnabled(!!data.photo_recognition)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!record) return;
    const m = ["photo", "per_100g", "direct"].includes(record.method)
      ? record.method : (record.items?.[0]?.mode || "direct");
    setMode(m);
    setItems((record.items || []).map((it) => ({
      name: it.name || "", mode: it.mode,
      carbs_per_100g: it.carbs_per_100g ?? "", grams: it.grams ?? "",
      direct_carbs: it.direct_carbs ?? "", photo_carbs: it.photo_carbs ?? null,
      _grams: it.grams ?? "", _note: it.note || "",
    })));
    const d = new Date(record.datetime);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    setDt(d.toISOString().slice(0, 16));
    setNotes(record.notes || "");
    if (record.method === "photo") setAiResult(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function switchMode(m) {
    setMode(m);
    // Preserve typed name across modes where possible.
    setItems((prev) =>
      prev.map((it) => ({ ...it, mode: m }))
    );
  }

  function itemCarbs(it) {
    if (it.mode === "per_100g") {
      const c = parseNum(it.carbs_per_100g);
      const g = parseNum(it.grams);
      if (c == null || g == null || g <= 0 || c > 100 || c < 0) return null;
      return (c * g) / 100;
    }
    if (it.mode === "direct") {
      const d = parseNum(it.direct_carbs);
      if (d == null || d < 0) return null;
      return d;
    }
    if (it.mode === "photo") {
      const c = parseNum(it.carbs_per_100g);
      const g = parseNum(it.grams);
      if (c != null && g != null && g > 0 && c >= 0 && c <= 100) return (c * g) / 100;
      return it.photo_carbs != null ? parseNum(it.photo_carbs) : null;
    }
    return null;
  }

  const total = items.reduce((acc, it) => {
    const c = itemCarbs(it);
    return acc + (c != null ? c : 0);
  }, 0);

  function updateItem(idx, patch) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function addItem() {
    setItems((prev) => [...prev, emptyItem(mode)]);
  }
  function removeItem(idx) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Ficheiro inválido. Escolha uma imagem.");
      return;
    }
    setPhoto(URL.createObjectURL(f));
    setPhotoFile(f);
    setDemoResult(false);
    setAiResult(false);
    setExtras(null);
  }

  async function analyzePhoto() {
    if (!photoFile) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("image", photoFile);
      fd.append("mode", photoMode);
      const { data } = await api.post(`/profiles/${activeProfileId}/meals/analyze-photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.could_not_estimate || !data.items?.length) {
        toast.error(data.reason || "Não foi possível estimar a partir desta fotografia.");
        return;
      }
      setItems(data.items.map((it) => ({
        ...emptyItem("photo"), name: it.name,
        carbs_per_100g: it.carbs_per_100g ?? (it.carbs != null && it.grams ? +(it.carbs / it.grams * 100).toFixed(1) : ""),
        grams: it.grams ?? "", photo_carbs: null,
        _note: it.note || "", _official_per100g: it.official_carbs_per_100g ?? null,
        _source: it.source || null, _fdc: it.fdc_description || null,
      })));
      setExtras(data.extras || null);
      setExtrasSource(data.extras_source || null);
      setAiResult(true);
      setDemoResult(false);
      setShowExtras(false);
      toast.success(t("ai_done"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAnalyzing(false);
    }
  }

  function loadDemoExample() {
    // Fictional example, clearly independent from the photo. Not real recognition.
    setItems([
      { ...emptyItem("photo"), name: "Exemplo: Arroz cozido", photo_carbs: 28 },
      { ...emptyItem("photo"), name: "Exemplo: Peito de frango", photo_carbs: 0 },
    ]);
    setExtras({ energy_kcal: 180, protein_g: null, fat_g: null, saturated_fat_g: null, sugars_g: null, fiber_g: null, salt_g: null });
    setExtras(null);
    setExtrasSource(null);
    setDemoResult(true);
    setAiResult(false);
    setShowExtras(false);
    toast.message(t("photo_demo_note"));
  }

  async function save() {
    const payloadItems = items
      .map((it) => {
        if (it.mode === "per_100g")
          return { name: it.name, mode: "per_100g", carbs_per_100g: parseNum(it.carbs_per_100g), grams: parseNum(it.grams) };
        if (it.mode === "direct")
          return { name: it.name, mode: "direct", direct_carbs: parseNum(it.direct_carbs) };
        return { name: it.name, mode: "photo", carbs_per_100g: parseNum(it.carbs_per_100g),
                 grams: parseNum(it.grams),
                 photo_carbs: (parseNum(it.carbs_per_100g) != null && parseNum(it.grams) != null)
                   ? +((parseNum(it.carbs_per_100g) * parseNum(it.grams)) / 100).toFixed(2)
                   : parseNum(it.photo_carbs) };
      })
      .filter((it) => {
        if (it.mode === "per_100g") return it.carbs_per_100g != null && it.grams != null;
        if (it.mode === "direct") return it.direct_carbs != null;
        return it.photo_carbs != null;
      });

    if (payloadItems.length === 0) {
      toast.error(t("meal_invalid"));
      return;
    }
    setSaving(true);
    try {
      const d = new Date(dt);
      const body = { datetime: d.toISOString(), notes, method: mode, items: payloadItems };
      if (record) await api.patch(`/profiles/${activeProfileId}/meals/${record.id}`, body);
      else await api.post(`/profiles/${activeProfileId}/meals`, body);
      toast.success(record ? t("record_updated") : t("meal_saved"));
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5" data-testid="meal-form">
      {/* Mode selector */}
      <div className="grid grid-cols-3 gap-2">
        {MODES.map((m) => (
          <button
            key={m.id}
            onClick={() => switchMode(m.id)}
            data-testid={`meal-mode-${m.id}`}
            className={`rounded-xl px-2 py-3 text-xs sm:text-sm font-semibold border transition-all ${
              mode === m.id
                ? "bg-[#1A73E8] text-white border-[#1A73E8] shadow-sm"
                : "bg-[#EBF3FA] text-[#0D253F] border-[#B8D4F8] hover:bg-[#DCEBFE]"
            }`}
          >
            {t(m.key)}
          </button>
        ))}
      </div>

      {/* Photo mode */}
      {mode === "photo" && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setPhotoMode("single"); setItems([emptyItem("photo")]); setAiResult(false); setDemoResult(false); }}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${photoMode === "single" ? "border-[#1A73E8] bg-[#1A73E8] text-white" : "border-[#B8D4F8] bg-[#EBF3FA] text-[#0D253F]"}`}
              data-testid="meal-photo-single"
            >
              {t("photo_single")}
            </button>
            <button
              type="button"
              onClick={() => { setPhotoMode("plate"); setItems([emptyItem("photo")]); setAiResult(false); setDemoResult(false); }}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${photoMode === "plate" ? "border-[#1A73E8] bg-[#1A73E8] text-white" : "border-[#B8D4F8] bg-[#EBF3FA] text-[#0D253F]"}`}
              data-testid="meal-photo-plate"
            >
              {t("photo_plate")}
            </button>
          </div>
          <p className="text-xs text-[#627D98]">{photoMode === "single" ? t("photo_single_hint") : t("photo_plate_hint")}</p>

          {photo && (
            <div className="relative">
              <img src={photo} alt="Refeição" className="w-full h-44 object-cover rounded-xl" />
              <button
                onClick={() => { setPhoto(null); setPhotoFile(null); setDemoResult(false); setAiResult(false); setExtras(null); }}
                className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow"
                data-testid="meal-photo-remove"
                aria-label={t("photo_remove")}
              >
                <Trash2 size={16} className="text-[#C5221F]" />
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center justify-center gap-2 rounded-xl border border-[#B8D4F8] bg-[#EBF3FA] px-3 py-3 text-sm font-semibold text-[#0D253F] cursor-pointer" data-testid="meal-photo-take">
              <Camera size={18} /> {t("photo_take")}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} />
            </label>
            <label className="flex items-center justify-center gap-2 rounded-xl border border-[#B8D4F8] bg-[#EBF3FA] px-3 py-3 text-sm font-semibold text-[#0D253F] cursor-pointer" data-testid="meal-photo-gallery">
              <ImageIcon size={18} /> {t("photo_gallery")}
              <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
            </label>
          </div>

          {aiEnabled ? (
            <>
              <div className="flex items-start gap-2 rounded-xl px-3 py-2.5 text-sm" style={{ background: "#E6F4EA", border: "1px solid #A8DADC", color: "#137333" }}>
                <Sparkles size={16} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">{t("ai_on")}</p>
                  <p className="text-xs mt-0.5">{t("ai_note")}</p>
                </div>
              </div>
              <Button
                disabled={!photo || analyzing}
                onClick={analyzePhoto}
                className="w-full h-11 bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold"
                data-testid="meal-photo-analyze-btn"
              >
                {analyzing ? (<><Loader2 size={16} className="mr-1.5 animate-spin" /> {t("ai_analyzing")}</>) : (<><Sparkles size={16} className="mr-1.5" /> {t("ai_analyze")}</>)}
              </Button>
            </>
          ) : (
            <>
              <div className="flex items-start gap-2 rounded-xl px-3 py-2.5 text-sm" style={{ background: "#FEF7E0", border: "1px solid #FDE293", color: "#8a5a00" }}>
                <Lock size={16} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">{t("photo_not_ready")}</p>
                  <p className="text-xs mt-0.5">{t("photo_demo_note")}</p>
                </div>
              </div>
              <Button
                variant="outline"
                disabled={!photo}
                onClick={loadDemoExample}
                className="w-full"
                data-testid="meal-photo-demo-btn"
              >
                {t("photo_demo")}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Items (per_100g / direct / photo-result) */}
      {(mode !== "photo" || demoResult || aiResult) && (
        <div className="space-y-4">
          {demoResult && (
            <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold" style={{ background: "#FEF7E0", color: "#8a5a00", border: "1px solid #FDE293" }}>
              DADOS FICTÍCIOS
            </div>
          )}
          {aiResult && (
            <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold" style={{ background: "#E3EFF9", color: "#0D3C78", border: "1px solid #B8D4F8" }} data-testid="ai-estimate-badge">
              <Sparkles size={12} /> {t("ai_badge")}
            </div>
          )}
          {items.map((it, idx) => (
            <div key={idx} className="rounded-xl border border-[#E3EFF9] p-3 space-y-3 bg-[#FAFCFE]">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Label className="text-xs text-[#627D98]">{t("meal_food")}</Label>
                  <Input
                    value={it.name}
                    onChange={(e) => updateItem(idx, { name: e.target.value })}
                    placeholder={t("meal_food")}
                    data-testid={`meal-item-name-${idx}`}
                  />
                </div>
                {items.length > 1 && (
                  <button onClick={() => removeItem(idx)} className="mt-5 text-[#C5221F]" data-testid={`meal-item-remove-${idx}`} aria-label={t("remove")}>
                    <Trash2 size={18} />
                  </button>
                )}
              </div>

              {it.mode === "per_100g" && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-[#627D98]">{t("meal_hc100")}</Label>
                    <Input inputMode="decimal" value={it.carbs_per_100g} onChange={(e) => updateItem(idx, { carbs_per_100g: e.target.value })} data-testid={`meal-hc100-${idx}`} />
                  </div>
                  <div>
                    <Label className="text-xs text-[#627D98]">{t("meal_weight")}</Label>
                    <Input inputMode="decimal" value={it.grams} onChange={(e) => updateItem(idx, { grams: e.target.value })} data-testid={`meal-grams-${idx}`} />
                  </div>
                  {parseNum(it.carbs_per_100g) != null && parseNum(it.grams) != null && (
                    <p className="col-span-2 text-xs text-[#3B526B]">
                      {t("meal_formula")}: {parseNum(it.carbs_per_100g)} × {parseNum(it.grams)} ÷ 100 ={" "}
                      <strong>{itemCarbs(it) != null ? itemCarbs(it).toFixed(2).replace(/\.?0+$/, "") : "—"} {t("grams")} HC</strong>
                    </p>
                  )}
                </div>
              )}

              {it.mode === "direct" && (
                <div>
                  <Label className="text-xs text-[#627D98]">{t("meal_direct")}</Label>
                  <Input inputMode="decimal" value={it.direct_carbs} onChange={(e) => updateItem(idx, { direct_carbs: e.target.value })} data-testid={`meal-direct-${idx}`} />
                </div>
              )}

              {it.mode === "photo" && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-[#627D98]">Hidratos por 100 g (g)</Label>
                    <Input inputMode="decimal" value={it.carbs_per_100g ?? ""} onChange={(e) => updateItem(idx, { carbs_per_100g: e.target.value })} data-testid={`meal-photo-hc100-${idx}`} />
                  </div>
                  <div>
                    <Label className="text-xs text-[#627D98]">Peso (g)</Label>
                    <Input inputMode="decimal" value={it.grams ?? ""} onChange={(e) => updateItem(idx, { grams: e.target.value })} data-testid={`meal-photo-grams-${idx}`} />
                  </div>
                  {parseNum(it.carbs_per_100g) != null && parseNum(it.grams) != null && (
                    <p className="col-span-2 text-xs text-[#3B526B]">
                      {parseNum(it.carbs_per_100g)} × {parseNum(it.grams)} ÷ 100 ={" "}
                      <strong>{itemCarbs(it) != null ? itemCarbs(it).toFixed(2).replace(/\.?0+$/, "") : "—"} {t("grams")} HC</strong>
                    </p>
                  )}
                  {it._note && <p className="col-span-2 text-xs text-[#627D98]">{it._note}</p>}
                  {it._official_per100g != null && (
                    <p className="col-span-2 text-xs text-[#137333]" data-testid={`meal-official-${idx}`}>
                      ✓ Confirmação {it._source}: {it._official_per100g} g HC / 100 g{it._fdc ? ` · ${it._fdc}` : ""}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}

          {(mode !== "photo" || aiResult || demoResult) && (
            <button onClick={addItem} className="flex items-center gap-1.5 text-sm font-semibold text-[#1A73E8]" data-testid="meal-add-item">
              <Plus size={16} /> {t("meal_add_item")}
            </button>
          )}

          {(demoResult || aiResult) && (
            <div>
              <button onClick={() => setShowExtras((s) => !s)} className="flex items-center gap-1 text-sm font-semibold text-[#3B526B]" data-testid="meal-extras-toggle">
                <ChevronDown size={16} className={showExtras ? "rotate-180 transition" : "transition"} /> {t("extras")}
              </button>
              {showExtras && (
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  {EXTRA_DEFS.map(([key, label, unit]) => {
                    const v = extras ? extras[key] : null;
                    return (
                      <div key={key} className="rounded-lg bg-white border border-[#E3EFF9] px-3 py-2">
                        <div className="text-[#627D98] text-xs">{label}</div>
                        <div className="text-[#0D253F] font-medium">{v != null ? `${v} ${unit}` : t("not_available")}</div>
                      </div>
                    );
                  })}
                  <p className="col-span-2 text-xs text-[#627D98]">{extrasSource ? `Fonte: ${extrasSource}` : (aiResult ? t("ai_note") : t("photo_demo_note"))}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Common fields */}
      <div className="grid grid-cols-1 gap-3">
        <div>
          <Label className="text-xs text-[#627D98]">{t("meal_datetime")}</Label>
          <Input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="meal-datetime" />
        </div>
        <div>
          <Label className="text-xs text-[#627D98]">{t("meal_notes")} ({t("optional")})</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="meal-notes" />
        </div>
      </div>

      {/* Review */}
      <div className="rounded-xl bg-[#E3EFF9] px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-xs text-[#627D98]">{t("meal_total")}</div>
          <div className="text-2xl font-head font-bold text-[#0D253F]" data-testid="meal-total">
            {total.toFixed(2).replace(/\.?0+$/, "")} {t("grams")}
          </div>
        </div>
        <div className="text-xs text-[#627D98] text-right">
          {t("meal_method_used")}: <strong className="text-[#3B526B]">{t(MODES.find((m) => m.id === mode).key)}</strong>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="w-full h-12 bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold" data-testid="meal-save">
        {saving ? t("loading") : t("save")}
      </Button>
    </div>
  );
}
